import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, Unlock, Compass, Gauge, MessageCircle, FolderCheck,
  Globe, RefreshCw, Eye, Plug, Radar, ShoppingBag,
} from "lucide-react";

// PATCH 34 — /features. Benefit-first, charter-safe wording (no "compliant/certified/guaranteed").
// Icons are single-colour line icons (text-secondary), decorative-only: the headline carries meaning (L16).
const FEATURES = [
  { icon: ShieldCheck, testid: "feature-fixed-price", headline: "A clear price estimate up front",
    body: "Know your build's estimated cost before you commit. Scope and price are agreed up front — no drip pricing and no surprises mid-build." },
  { icon: Unlock, testid: "feature-your-code", headline: "Your code, always yours to take",
    body: "Export your full source code, free, on every plan including the free tier. Leave whenever you want. Nothing is held hostage to keep you subscribed." },
  { icon: Compass, testid: "feature-beco", headline: "The BeCo Index*", footnote: true,
    body: "A patent-pending guide comparing prompt clarity to build complexity — showing how the clarity of your idea shapes what it actually costs and takes to build, in plain language, not a hidden score." },
  { icon: Gauge, testid: "feature-compliance-picture", headline: "See your real compliance picture",
    body: "The Perimeter Gate and Compliance Risk Index turn dozens of regulatory questions into a plain-English readout of what your project actually needs — never a black box, never a guess." },
  { icon: MessageCircle, testid: "feature-coach", headline: "A build partner that actually listens",
    body: "Kadana's Coach explains why it's suggesting something, remembers what you've told it, and visibly quiets down the moment you say you've heard enough of a particular kind of suggestion." },
  { icon: FolderCheck, testid: "feature-evidence-pack", headline: "Walk into any audit with real proof",
    body: "Every project gets an Evidence Pack: a real, exportable record of what was checked and when — something you can actually hand to an investor, a regulator or your own counsel." },
  { icon: Globe, testid: "feature-anywhere", headline: "Tell us your idea. We'll tell you what it needs, anywhere",
    body: "Pick your sector and your market — UK, Kenya, Brazil, wherever — and see the real connectors, compliance considerations and identity providers relevant to building there, instantly." },
  { icon: RefreshCw, testid: "feature-switch-market", headline: "Built for one country? Take it to the next",
    body: "Switch Market lets you re-target an app you've already built for a new region, re-using what still applies and adding only what's genuinely different — not a rebuild from zero." },
  { icon: Eye, testid: "feature-nudge-ledger", headline: "Every AI suggestion, logged and visible to you",
    body: "The Nudge Transparency Ledger shows exactly what our Coach suggested, when, and why — nothing our AI does to help you build happens where you can't see it." },
  { icon: Plug, testid: "feature-connectors", headline: "Real connections, not empty promises",
    body: "Thousands of catalogued banking, payment, identity and logistics connectors across more than 60 countries — and every single one is labelled honestly by how ready it actually is to use." },
  { icon: Radar, testid: "feature-radar", headline: "We watch the rules so you don't have to",
    body: "Regulatory Radar tracks changes in the laws that affect your app and tells you plainly when something's shifted — never silently, never buried in fine print." },
  { icon: ShoppingBag, testid: "feature-marketplace", headline: "A fair marketplace, not a walled garden",
    body: "Buy or sell verified building blocks with other Kadana builders, with revenue splits that are visible and fixed, not something that changes on you later." },
];

export default function Features() {
  const nav = useNavigate();
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }} data-testid="features-page">
      <PublicNav />
      <div style={{ margin: "0 auto", padding: "56px 24px" }}>
        <h1 className="k-heading" style={{ fontSize: "clamp(36px,5vw,56px)", letterSpacing: "-.03em", margin: 0 }}>What Kadana gives you</h1>
        <p className="k-muted" style={{ fontSize: 15, marginTop: 8, maxWidth: 640 }}>
          Everything you need to go from a plain-English idea to compliance-ready software — in the open, on a fixed price.
        </p>

        <div data-testid="features-grid" style={{
          display: "grid", gap: 18, marginTop: 40,
          gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
        }}>
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.testid} data-testid={f.testid} className="k-container k-accent-card"
                style={{ padding: 22, display: "flex", flexDirection: "column", gap: 10 }}>
                <Icon size={22} color="var(--muted)" strokeWidth={1.75} aria-hidden="true" />
                <h2 className="k-heading" style={{ fontSize: 17, margin: 0, lineHeight: 1.3 }}>{f.headline}</h2>
                <p className="k-muted" style={{ fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>{f.body}</p>
                {f.footnote && (
                  <p className="k-muted" style={{ fontSize: 11, marginTop: "auto" }}>
                    *Patent application filed, Kenya, 13 September 2026.{" "}
                    <a className="k-link" data-testid="features-patent-disclaimer-link" onClick={() => nav("/disclaimer")} style={{ cursor: "pointer" }}>See disclaimer.</a>
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 44, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <p className="k-muted" style={{ fontSize: 15, margin: 0 }}>
            Try the instant Build Preview on our home page, or start building free — no card needed.
          </p>
          <button className="k-btn k-btn-primary" data-testid="features-start-free" onClick={() => nav("/register")}>Start free</button>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
